import { Delete, Edit, ManageAccounts, Publish, Replay } from '@mui/icons-material';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import { Box, IconButton, Tooltip, Typography, Checkbox } from '@mui/material';
import WO2Button from '../../../../common/components/button/Button';
import { AnyAction } from '@reduxjs/toolkit';
import React, { useState, useMemo, useCallback } from 'react';
import { ClientSideDataTable } from '../../../../common/components/dataTable/clientSide';
import { DatatableColumn, FilterDataType } from '../../../../common/components/dataTable/types';
import { useConfirmation } from '../../../../common/components/dialogs';
import { LoadingProgress } from '../../../../common/store/types';
import history from '../../../../history';
import { ProcessClientDialog } from '../../common/components/processClientDialog';
import { OnboardingWizardStatus } from '../store/enum';
import { OnboardingWizards, ResetStatusPayload } from '../store/types';
import { makeStyles } from '@mui/styles';

const useLocalStyles = makeStyles({
  rowNoCheckbox: {
    '& td:first-child': {
      visibility: 'hidden',
      pointerEvents: 'none',
    },
  },
});

export interface OnboardingsProps {
  hideAllActions: boolean;
  onboardings: OnboardingWizards[];
  loadingProgress: LoadingProgress;
  deleteOnboarding: (id: string) => void;
  resumeOnboarding: (client: OnboardingWizards) => void;
  resubmitProgress: LoadingProgress;
  resubmitClient: (id: string) => Promise<AnyAction>;
  resetStatus: (payload: ResetStatusPayload) => Promise<AnyAction>;
  resetStatusProgress: LoadingProgress;
  hasClientEditAdminOnlyPermission: boolean;
  fetchOnboardingWizardsModels: () => Promise<AnyAction>;
  bulkProcessClient: (ids: string[]) => Promise<AnyAction>;
  bulkProcessClientsProgress: LoadingProgress;
}

export const OnboardingTable = (props: OnboardingsProps): JSX.Element => {
  const localClasses = useLocalStyles();
  const confirm = useConfirmation();
  const {
    onboardings,
    hasClientEditAdminOnlyPermission,
    loadingProgress,
    deleteOnboarding,
    hideAllActions,
    resumeOnboarding,
    resubmitClient,
    resubmitProgress,
    resetStatus,
    resetStatusProgress,
    fetchOnboardingWizardsModels,
    bulkProcessClient,
    bulkProcessClientsProgress,
  } = props;

  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const [isBulkProcessDialogOpen, setIsBulkProcessDialogOpen] = useState<boolean>(false);
  const [dialogSelectedRows, setDialogSelectedRows] = useState<number[]>([]);

  const selectedSet = useMemo(() => new Set(selectedRows), [selectedRows]);

  const isRowSelectable = useCallback(
    (dataIndex: number) => OnboardingWizardStatus.getById(onboardings[dataIndex]?.statusId) === OnboardingWizardStatus.Active,
    [onboardings]
  );

  const setRowProps = useCallback(
    (_row: unknown, dataIndex: number) => (isRowSelectable(dataIndex) ? {} : { className: localClasses.rowNoCheckbox }),
    [isRowSelectable, localClasses.rowNoCheckbox]
  );

  const customToolbar = useCallback(
    ({ displayData }: { displayData: { dataIndex: number }[] }) => {
      const visibleActiveIndices = displayData
        .filter((row) => isRowSelectable(row.dataIndex))
        .map((row) => row.dataIndex);
      const visibleActiveSet = new Set(visibleActiveIndices);
      const allVisibleActiveSelected = visibleActiveIndices.length > 0 && visibleActiveIndices.every((i) => selectedSet.has(i));
      const anyVisibleActiveSelected = visibleActiveIndices.some((i) => selectedSet.has(i));
      const hasSelection = selectedRows.length > 0;
      return (
        <>
          {!loadingProgress?.isLoading && hasSelection && (
            <WO2Button
              color={'primary'}
              style={{ marginRight: '8px', padding: '6px 16px' }}
              disableElevation
              variant="contained"
              data-testid="processClientsButton"
              onClick={() => {
                setDialogSelectedRows(selectedRows);
                setIsBulkProcessDialogOpen(true);
              }}
            >
              <Typography variant="inherit">Process Clients</Typography>
            </WO2Button>
          )}
          {!loadingProgress?.isLoading && (
            <Tooltip title={allVisibleActiveSelected ? 'Deselect all' : 'Select all Active'}>
              <Checkbox
                sx={{ padding: '12px' }}
                data-testid="selectAllCheckbox"
                checked={allVisibleActiveSelected}
                indeterminate={anyVisibleActiveSelected && !allVisibleActiveSelected}
                onChange={() =>
                  setSelectedRows(
                    allVisibleActiveSelected
                      ? selectedRows.filter((i) => !visibleActiveSet.has(i))
                      : [...new Set([...selectedRows, ...visibleActiveIndices])]
                  )
                }
              />
            </Tooltip>
          )}
          {!loadingProgress?.isLoading && addButton()}
        </>
      );
    },
    [selectedSet, selectedRows, isRowSelectable, loadingProgress?.isLoading, localClasses.rowNoCheckbox]
  );

  const [isResubmitDialogOpen, setIsResubmitDialogOpen] = useState<boolean>(false);
  const [id, setId] = useState<string | undefined>(undefined);

  const nameColumn = (dataIndex: number): React.ReactNode => (
    <Typography data-testid="name" variant="h5">
      {props.onboardings[dataIndex]?.name}
    </Typography>
  );

  const typeColumn = (dataIndex: number): React.ReactNode => (
    <Typography data-testid="type" variant="h5">
      {props.onboardings[dataIndex]?.type}
    </Typography>
  );

  const adviserNameColumn = (dataIndex: number): React.ReactNode => (
    <Typography data-testid="adviser" variant="h5">
      {props.onboardings[dataIndex]?.adviserName}
    </Typography>
  );

  const contactColumn = (dataIndex: number): React.ReactNode => (
    <Typography data-testid="contact" variant="h5">
      {props.onboardings[dataIndex]?.contact}
    </Typography>
  );

  const createdDateColumn = (dataIndex: number): React.ReactNode => (
    <Typography data-testid="createdDate" variant="h5">
      {props.onboardings[dataIndex]?.createdOn}
    </Typography>
  );

  const lastUpdatedDateColumn = (dataIndex: number): React.ReactNode => (
    <Typography data-testid="lastUpdatedDate" variant="h5">
      {props.onboardings[dataIndex]?.lastUpdatedOn}
    </Typography>
  );

  const statusColumn = (dataIndex: number): React.ReactNode => (
    <Typography data-testid="status" variant="h5">
      {OnboardingWizardStatus.getById(props.onboardings[dataIndex]?.statusId)?.displayName}
    </Typography>
  );

  const actionsColumn = (dataIndex: number): React.ReactNode => {
    const status = OnboardingWizardStatus.getById(props.onboardings[dataIndex]?.statusId);
    switch (status) {
      case OnboardingWizardStatus.Active:
        return (
          <>
            <IconButton
              disableFocusRipple
              disableRipple
              onClick={() => {
                resumeOnboarding(props.onboardings[dataIndex]);
              }}
              data-testid={`editButton_${dataIndex}`}
            >
              <Edit color={'primary'} />
            </IconButton>
            <IconButton
              disableFocusRipple
              disableRipple
              onClick={() => {
                const thisId = props.onboardings[dataIndex].id;
                if (!!thisId) {
                  confirm({
                    title: 'Delete client',
                    description: 'Are you sure you wish to delete this client?',
                  }).then(() => {
                    deleteOnboarding(thisId);
                  });
                }
              }}
              data-testid={`deleteButton_${dataIndex}`}
            >
              <Delete color={'primary'} />
            </IconButton>
          </>
        );
      case OnboardingWizardStatus.Completed:
        return (
          <IconButton
            disableFocusRipple
            disableRipple
            onClick={() => {
              const clientId = props.onboardings[dataIndex].clientId;
              history.push(`/client/${clientId}/details/accounts`);
            }}
            data-testid={`manageButton_${dataIndex}`}
          >
            <ManageAccounts color={'primary'} />
          </IconButton>
        );
      case OnboardingWizardStatus.Error:
        return hasClientEditAdminOnlyPermission ? (
          <>
            <Tooltip title="Resubmit client">
              <IconButton
                disableFocusRipple
                disableRipple
                onClick={() => {
                  const thisId = props.onboardings[dataIndex].id;
                  if (!!thisId) {
                    setId(thisId);
                    setIsResubmitDialogOpen(true);
                  }
                }}
                data-testid={`resubmitButton_${dataIndex}`}
              >
                <Publish color={'primary'} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reset client status to Active">
              <IconButton
                disableFocusRipple
                disableRipple
                onClick={() => {
                  const client = props.onboardings[dataIndex];
                  confirm({
                    title: 'Reset onboarding status',
                    description: 'Are you sure you wish to reset the status of this client?',
                    description2:
                      'This action is permanent, please ensure you have selected the correct client before proceeding.',
                  }).then(() => {
                    resetStatus({ id: client.id, clientId: client?.clientId });
                  });
                }}
                data-testid={`resetButton_${dataIndex}`}
              >
                <Replay color={'primary'} />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <></>
        );
    }
  };

  const addButton = (): React.ReactNode => (
    <Tooltip title="Refresh">
      <RefreshRounded
        style={{ marginTop: '8px', marginRight: '6px', cursor: 'pointer', color: '#757575' }}
        onClick={() => {
          fetchOnboardingWizardsModels();
        }}
      ></RefreshRounded>
    </Tooltip>
  );

  const columns: DatatableColumn[] = [
    {
      filterDataType: FilterDataType.string,
      name: 'name',
      label: 'CLIENT NAME',
      options: {
        filter: true,
        sort: true,
        customBodyRenderLite: (dataIndex: number): React.ReactNode => nameColumn(dataIndex),
      },
    },
    {
      filterDataType: FilterDataType.string,
      name: 'type',
      label: 'TYPE',
      options: {
        filter: true,
        sort: false,
        customBodyRenderLite: (dataIndex: number): React.ReactNode => typeColumn(dataIndex),
      },
    },
    {
      filterDataType: FilterDataType.string,
      name: 'contact',
      label: 'CONTACT',
      options: {
        filter: true,
        sort: false,
        customBodyRenderLite: (dataIndex: number): React.ReactNode => contactColumn(dataIndex),
      },
    },
    {
      filterDataType: FilterDataType.string,
      name: 'adviserName',
      label: 'ADVISER NAME',
      options: {
        filter: true,
        sort: false,
        customBodyRenderLite: (dataIndex: number): React.ReactNode => adviserNameColumn(dataIndex),
      },
    },
    {
      name: 'createdOn',
      textAlign: 'right',
      label: 'CREATED',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex: number): React.ReactNode => createdDateColumn(dataIndex),
      },
    },
    {
      name: 'lastUpdatedOn',
      textAlign: 'right',
      label: 'LAST UPDATED',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex: number): React.ReactNode => lastUpdatedDateColumn(dataIndex),
      },
    },
    {
      filterDataType: FilterDataType.enumeration,
      enumerationType: OnboardingWizardStatus,
      name: 'statusId',
      label: 'STATUS',
      options: {
        filter: true,
        sort: true,
        customBodyRenderLite: (dataIndex: number): React.ReactNode => statusColumn(dataIndex),
      },
      renderFilterChipValue: (statusId: string) => {
        return OnboardingWizardStatus.getById(+statusId)?.displayName || 'n/a';
      },
    },
    {
      name: 'actions',
      label: ' ',
      textAlign: 'right',
      options: {
        filter: false,
        sort: false,
        viewColumns: false,
        customBodyRenderLite: (dataIndex: number): React.ReactNode => actionsColumn(dataIndex),
        setCellProps: () => {
          return {
            style: {
              minWidth: '100px',
              textAlign: 'right',
            },
          };
        },
      },
    },
  ];

  return (
    <Box paddingTop={'20px'}>
      <ClientSideDataTable
        loadingProgress={{
          isLoading: loadingProgress?.isLoading || resetStatusProgress?.isLoading,
          hasErrors: loadingProgress?.hasErrors || resetStatusProgress?.hasErrors,
        }}
        columns={hideAllActions ? columns.filter((c) => c.name !== 'actions') : columns}
        data={onboardings}
        options={{
          filter: true,
          pagination: true,
          selectableRows: 'multiple',
          selectableRowsHideCheckboxes: false,
          selectableRowsHeader: false,               
          selectToolbarPlacement: 'none',
          rowsSelected: selectedRows,
          onRowSelectionChange: (_current, all) => setSelectedRows(all.map((r) => r.dataIndex)),
          isRowSelectable,
          setRowProps,
          customToolbar,
        }}
      />
      <ProcessClientDialog
        isOpen={isResubmitDialogOpen}
        saveProgress={resubmitProgress}
        handleClose={() => setIsResubmitDialogOpen(false)}
        handleProceedButtonClick={async () => {
          if (!!id) {
            resubmitClient(id).then(() => setIsResubmitDialogOpen(false));
          }
        }}
      />
      <ProcessClientDialog
        isOpen={isBulkProcessDialogOpen}
        saveProgress={bulkProcessClientsProgress}
        title={`Are you sure you wish to submit the following ${dialogSelectedRows.length} client${dialogSelectedRows.length === 1 ? '' : 's'}?`}
        handleClose={() => setIsBulkProcessDialogOpen(false)}
        handleProceedButtonClick={() => {
          const selectedSet = new Set(dialogSelectedRows);
          const ids = onboardings
            .filter((_, i) => selectedSet.has(i))
            .map((o) => o.id)
            .filter(Boolean) as string[];
          bulkProcessClient(ids).then(() => {
            setIsBulkProcessDialogOpen(false);
            setSelectedRows([]);
          });
        }}
      >
        {(() => {
          const selectedSet = new Set(dialogSelectedRows);
          const selectedClients = onboardings.filter((_, i) => selectedSet.has(i));
          const byAdviser = selectedClients.reduce<Record<string, number>>((acc, client) => {
            const adviser = client.adviserName || 'Unknown';
            acc[adviser] = (acc[adviser] ?? 0) + 1;
            return acc;
          }, {});
          return (
            <Box marginTop={'16px'} marginBottom={'8px'}>
              {Object.entries(byAdviser).map(([adviser, count]) => (
                <Typography key={adviser} variant="body2">
                  {`Adviser ${adviser}: ${count} client${count === 1 ? '' : 's'}`}
                </Typography>
              ))}
            </Box>
          );
        })()}
      </ProcessClientDialog>
    </Box>
  );
};
